import React, { FC, Fragment } from 'react'
import { GuildAccessPill } from 'src/molecules/GuildAccessPill'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { slugs } from '@/constants/slugs'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { LandscapeRowCollection, LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import { useGuildMember } from '@/services/GuildUserService'
import { Episode, Project } from '@/services/ProjectsService'
import { LinkViewModel } from '@/services/RenderService'
import { isAPitch, isASneakPeek, isATeaser, isATorch } from '@/utils/EpisodeUtil'
import { useSafeTrack } from '@/utils/analytics'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { ResumeWatchingEdge } from '../ContinueWatchingCollection/data'

interface Props {
  contentLinks: LinkViewModel[]
  title: string
  toContinueWatching?: ResumeWatchingEdge[]
}

export const StartWatchingCollection: FC<Props> = ({ contentLinks, title, toContinueWatching }) => {
  const track = useSafeTrack()
  const { isGuildMember } = useGuildMember()
  const { t } = useTranslate('watch')

  const userHasEarlyAccess = isGuildMember
  const toContinueWatchingIds = new Set(toContinueWatching?.map((item) => item?.node?.watchableGuid))

  const filteredLinks = contentLinks.filter((contentLink) => {
    const metadata = contentLink.metadata as { episode?: Episode; project?: Project }
    const episodeId = metadata?.episode?.guid

    return toContinueWatchingIds ? !toContinueWatchingIds.has(episodeId) : true
  })

  return (
    <LandscapeRowCollection testId="start-watching-collection" title={title}>
      {(filteredLinks.length > 3 ? filteredLinks : contentLinks).map((contentLink) => {
        const metadata = contentLink.metadata as { episode?: Episode; project?: Project }
        const episode = metadata?.episode
        const project = metadata?.project
        const displayTitle = episode && project ? getDisplayTitle({ episode, project }, t) ?? '' : ''
        const displaySubtitle = episode && project ? getDisplaySubtitle({ episode, project }) ?? '' : ''

        const handleTracking = () => {
          if (episode?.unavailableReason && episode?.earlyAccessDate) {
            track('Rail Guild Thumbnail Clicked')
          } else {
            track('Rail Thumbnail Clicked')
          }
        }

        return (
          <Fragment key={contentLink.imageUrl}>
            <LandscapeTileCard className="relative mb-2 mr-4" linkModel={contentLink} onClick={handleTracking}>
              <div className="absolute inset-0">
                <GuildAccessPill
                  isGuildMember={userHasEarlyAccess}
                  earlyAccessDate={episode?.earlyAccessDate ? new Date(episode.earlyAccessDate) : undefined}
                  unavailableReason={episode?.unavailableReason}
                />
                {episode?.earlyAccessDate && (
                  <GuildAccessNotch
                    earlyAccessDate={new Date(episode.earlyAccessDate)}
                    publiclyAvailableDate={
                      episode.publiclyAvailableDate ? new Date(episode.publiclyAvailableDate) : undefined
                    }
                    isGuildMember={isGuildMember}
                  />
                )}
              </div>
              {!episode?.unavailableReason && <PlayButtonThumbnailOverlay />}
            </LandscapeTileCard>
            <TitleAndSubtitleDisplay className="mr-4" subtitle={displaySubtitle} title={displayTitle} />
          </Fragment>
        )
      })}
    </LandscapeRowCollection>
  )
}

function getDisplayTitle({ episode, project }: { episode: Episode; project: Project }, t: TranslateFunction) {
  if (!episode) return null

  if (isATorch(episode)) {
    return getTorchTitle(t)
  } else if (isATeaser(episode)) {
    return getTeaserTitle(t)
  } else if (isAPitch(episode)) {
    return getPitchTitle(t)
  } else if (isASneakPeek(episode)) {
    return getSneakPeekTitle(t)
  } else if ('series' === project?.projectType) {
    return getSeriesTitle(episode, project, t)
  } else if ('movie' === project?.projectType) {
    return getMovieTitle(episode, t)
  } else if ('podcast' === project?.projectType) {
    return getPodcastTitle(t)
  } else if (project?.slug === slugs.dryBar && 'special' === project?.projectType) {
    return getSpecialTitle(t)
  } else {
    return null
  }
}

function getDisplaySubtitle({ episode, project }: { episode: Episode; project: Project }) {
  if (
    ('movie' === project?.projectType && episode?.isTrailer) ||
    isATeaser(episode) ||
    isATorch(episode) ||
    isAPitch(episode) ||
    isASneakPeek(episode)
  ) {
    return project?.name
  } else {
    return episode?.subtitle
  }
}

function getTorchTitle(t: TranslateFunction) {
  return t('torch', 'Torch')
}

function getTeaserTitle(t: TranslateFunction) {
  return t('teaser', 'Teaser')
}

function getPitchTitle(t: TranslateFunction) {
  return t('pitch', 'Pitch')
}

function getSeriesTitle(episode: Episode, project: Project, t: TranslateFunction) {
  if (episode?.isTrailer) {
    return t('trailer', 'Trailer')
  } else {
    return t('seasonEpisodeNumber', 'S{{seasonNumber}}:E{{episodeNumber}} {{projectName}}', {
      episodeNumber: episode.episodeNumber,
      seasonNumber: episode.seasonNumber,
      projectName: project.name ?? '',
    })
  }
}

function getMovieTitle(episode: Episode, t: TranslateFunction) {
  if (episode?.isTrailer) {
    return t('trailer', 'Trailer')
  } else {
    return t('film', 'Film')
  }
}

function getPodcastTitle(t: TranslateFunction) {
  return t('podcast', 'Podcast')
}

function getSpecialTitle(t: TranslateFunction) {
  return t('special', 'Special')
}

function getSneakPeekTitle(t: TranslateFunction) {
  return t('sneakPeek', 'Sneak Peek')
}
